import React from 'react';
import { Page, Text, View, Document, StyleSheet, PDFViewer, PDFDownloadLink, Font, Image } from '@react-pdf/renderer';
import { BrowserView, MobileView } from 'react-device-detect';
import MobilePdfView from 'ui-component/MobilePdfView';
import { Stack, Typography } from '@mui/material';
import logo from 'assets/images/Logo-Aone-Final-Full-1.png';
import moment from 'moment';

Font.register({
    family: 'Noto Sans Lao',
    src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@master/hinted/ttf/NotoSansLao/NotoSansLao-Regular.ttf'
});

const formatCurrency = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '₭0';
    return `₭${Number(num).toLocaleString('en-US')}`;
};

// Announcement targets these customer types; empty/null = applies to everyone.
const formatCustomerTypes = (types) => {
    if (!Array.isArray(types) || types.length === 0) return 'All';
    return types.join(', ');
};

// Bone-dry color cues so the printout matches the on-screen PackEditor "gift box".
const MATCH_COLOR = {
    SAME: { tint: '#FFE8EC', border: '#E11D48' },
    OR: { tint: '#E6F4FF', border: '#0C4DA2' },
    AND: { tint: '#EAF7E9', border: '#2E7D32' }
};

const styles = StyleSheet.create({
    body: { paddingTop: 30, paddingBottom: 60, paddingHorizontal: 35, fontFamily: 'Noto Sans Lao' },
    title: { padding: 5, fontSize: 20, textAlign: 'center', fontWeight: 'bold' },
    section: { flexDirection: 'row', alignItems: 'center', fontSize: 9, marginBottom: 2 },
    label: { width: 85, fontWeight: 'bold' },
    value: { flex: 1 },
    logo: { position: 'absolute', top: 20, left: 35, width: 80 },

    // Pack card
    packCard: {
        marginTop: 8,
        borderWidth: 1,
        borderColor: '#bbb',
        borderRadius: 4,
        overflow: 'hidden'
    },
    packHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 4,
        paddingHorizontal: 6
    },
    packHeaderText: { fontSize: 10, fontWeight: 'bold' },
    packBody: { paddingHorizontal: 8, paddingVertical: 6 },

    // Inner sub-table for products in a pack
    rowTable: { flexDirection: 'row', fontSize: 9, borderBottomWidth: 0.5, borderBottomColor: '#ddd', paddingVertical: 3 },
    headRow: { flexDirection: 'row', fontSize: 9, fontWeight: 'bold', backgroundColor: '#f4f4f4', paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: '#aaa' },

    colSeq: { width: '6%', paddingLeft: 4 },
    colName: { width: '54%', paddingLeft: 4 },
    colId: { width: '25%', paddingLeft: 4 },
    colMin: { width: '15%', paddingLeft: 4, textAlign: 'right', paddingRight: 4 },

    // Inner sub-table for premiums
    premRow: { flexDirection: 'row', fontSize: 9, borderBottomWidth: 0.5, borderBottomColor: '#ddd', paddingVertical: 3 },
    premHead: { flexDirection: 'row', fontSize: 9, fontWeight: 'bold', backgroundColor: '#fff7e6', paddingVertical: 4, borderBottomWidth: 1, borderBottomColor: '#e8b94f' },
    premSeq: { width: '6%', paddingLeft: 4 },
    premName: { width: '64%', paddingLeft: 4 },
    premId: { width: '20%', paddingLeft: 4 },
    premQty: { width: '10%', paddingLeft: 4, textAlign: 'right', paddingRight: 4 },

    summaryRow: { flexDirection: 'row', marginTop: 6, fontSize: 9 },
    summaryLabel: { fontWeight: 'bold', width: 110 },
    summaryValue: { flex: 1 }
});

const PackCard = ({ pack, index }) => {
    const color = MATCH_COLOR[pack.matchType] || MATCH_COLOR.SAME;
    return (
        <View style={[styles.packCard, { borderColor: color.border }]} wrap>
            <View style={[styles.packHeader, { backgroundColor: color.tint, borderBottomWidth: 1, borderBottomColor: color.border }]}>
                <Text style={[styles.packHeaderText, { color: color.border }]}>
                    {`#${index + 1}  ${pack.packId || 'Untitled'}`}
                </Text>
                <Text style={{ fontSize: 9, marginLeft: 8, color: color.border }}>[{pack.matchType}]</Text>
                {pack.packName ? (
                    <Text style={{ fontSize: 9, marginLeft: 8, color: '#444' }}>· {pack.packName}</Text>
                ) : null}
            </View>
            <View style={styles.packBody}>
                {/* Products */}
                <Text style={{ fontSize: 9, fontWeight: 'bold', marginBottom: 2 }}>Products</Text>
                <View style={styles.headRow}>
                    <Text style={styles.colSeq}>#</Text>
                    <Text style={styles.colName}>Product</Text>
                    <Text style={styles.colId}>Product ID</Text>
                    <Text style={styles.colMin}>{pack.matchType === 'AND' ? 'Min Qty' : ''}</Text>
                </View>
                {(pack.products || []).map((p, i) => (
                    <View key={i} style={styles.rowTable} wrap={false}>
                        <Text style={styles.colSeq}>{i + 1}</Text>
                        <Text style={styles.colName}>{`${p.model ? p.model + ' · ' : ''}${p.productName || ''}`}</Text>
                        <Text style={styles.colId}>{p.productId}</Text>
                        <Text style={styles.colMin}>{pack.matchType === 'AND' ? (p.minQty ?? 1) : ''}</Text>
                    </View>
                ))}

                {/* Summary */}
                <View style={styles.summaryRow}>
                    {pack.matchType !== 'AND' && (
                        <>
                            <Text style={styles.summaryLabel}>Required Qty:</Text>
                            <Text style={[styles.summaryValue, { marginRight: 14 }]}>{pack.requiredQty}</Text>
                        </>
                    )}
                    <Text style={styles.summaryLabel}>Cashback / pack:</Text>
                    <Text style={[styles.summaryValue, { marginRight: 14, color: '#c62828', fontWeight: 'bold' }]}>
                        {formatCurrency(pack.cashback)}
                    </Text>
                    <Text style={styles.summaryLabel}>Max Repeat:</Text>
                    <Text style={styles.summaryValue}>{pack.maxRepeat == null ? 'unlimited' : pack.maxRepeat}</Text>
                </View>

                {/* Premiums */}
                {(pack.premiums || []).length > 0 && (
                    <View style={{ marginTop: 6 }}>
                        <Text style={{ fontSize: 9, fontWeight: 'bold', marginBottom: 2 }}>Premiums (per pack)</Text>
                        <View style={styles.premHead}>
                            <Text style={styles.premSeq}>#</Text>
                            <Text style={styles.premName}>Premium</Text>
                            <Text style={styles.premId}>Premium ID</Text>
                            <Text style={styles.premQty}>Qty</Text>
                        </View>
                        {(pack.premiums || []).map((pr, i) => (
                            <View key={i} style={styles.premRow} wrap={false}>
                                <Text style={styles.premSeq}>{i + 1}</Text>
                                <Text style={styles.premName}>{pr.premiumName || pr.premium_id}</Text>
                                <Text style={styles.premId}>{pr.premium_id}</Text>
                                <Text style={styles.premQty}>{pr.qty}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {pack.remark ? (
                    <Text style={{ fontSize: 8, marginTop: 6, color: '#555' }}>
                        Remark: {pack.remark}
                    </Text>
                ) : null}
            </View>
        </View>
    );
};

const MyDocumentV2 = ({ data }) => (
    <Document>
        <Page size="A4" style={styles.body} wrap>
            <Image style={styles.logo} src={logo} />
            <View style={{ paddingTop: 10, paddingLeft: '55%' }}>
                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>{`Announcement No. ${data.announcementNo}`}</Text>
            </View>
            <View style={{ paddingTop: 5, paddingBottom: 10, paddingLeft: '55%' }}>
                <Text style={{ fontSize: 9, fontWeight: 'bold' }}>
                    {`PromotionDate: ${moment(data.startDate).format('DD-MM-yyyy')} - ${moment(data.endDate).format('DD-MM-yyyy')}`}
                </Text>
            </View>
            <Text style={styles.title}>Announcement Promotion (v2 · Auto-detect)</Text>
            <View style={{ paddingTop: 10, paddingBottom: 10, paddingRight: 10 }} wrap>
                <View style={{ paddingLeft: 5 }}>
                    <View style={styles.section}>
                        <Text style={styles.label}>To</Text>
                        <Text style={styles.value}>: {data.to}</Text>
                    </View>
                    <View style={styles.section}>
                        <Text style={styles.label}>Header</Text>
                        <Text style={styles.value}>: {data.header}</Text>
                    </View>
                    <View style={styles.section}>
                        <Text style={styles.label}>Customer Type</Text>
                        <Text style={styles.value}>: {formatCustomerTypes(data.customerTypes)}</Text>
                    </View>
                    {data.remark ? (
                        <View style={[styles.section, { paddingTop: 5, paddingBottom: 5 }]} wrap>
                            <Text style={{ fontSize: 9 }}>{`\t\t\t\t\t\t\t  ${data.remark}`}</Text>
                        </View>
                    ) : null}
                </View>
            </View>

            {/* Pack cards */}
            {(data.condition || []).map((pack, i) => (
                <PackCard key={i} pack={pack} index={i} />
            ))}

            {/* Signatures */}
            <View style={{ marginTop: 25, flexDirection: 'row', justifyContent: 'space-between' }} wrap={false}>
                <View style={{ width: '48%' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Verify By: {data.VerifyBy}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : {moment(data?.VerifyDate).format('DD-MM-yyyy')}</Text>
                </View>
                <View style={{ width: '48%' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Approve By: {data?.ApproveBy}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : {moment(data?.ApproveDate).format('DD-MM-yyyy')}</Text>
                </View>
            </View>

            <View style={{ marginTop: 20, borderWidth: 1, borderColor: 'blue', borderRadius: 10, padding: 10 }} wrap={false}>
                <Text style={{ color: 'blue', fontSize: 8, fontWeight: 'bold' }}>
                    A One Distributors Co., Ltd Nongbone Road, Naxay Village, Xaysettha District, Vientiane Lao PDR Mobile: 020 88871111
                </Text>
            </View>
        </Page>
    </Document>
);

const AnnouncementInvoicePDFV2 = ({ data }) => {
    if (!data) return null;
    return (
        <>
            <BrowserView style={{ width: '100%' }}>
                <PDFViewer style={{ width: '100%', height: '70vh' }}>
                    <MyDocumentV2 data={data} />
                </PDFViewer>
            </BrowserView>
            <MobileView style={{ width: '100%' }}>
                <MobilePdfView document={<MyDocumentV2 data={data} />} fileName={`Announcement_${data.announcementNo}`} />
            </MobileView>
        </>
    );
};

export default AnnouncementInvoicePDFV2;
